import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../context/I18nContext';
import BottomNav from '../components/BottomNav';
import LanguageToggle from '../components/LanguageToggle';
import UserAvatar from '../components/UserAvatar';
import { resolveMediaUrl } from '../lib/social';
import { formatAura } from '../lib/aura';

export default function DiscoverPage() {
  const { t } = useI18n();
  const [data, setData] = useState({ trending: [], creators: [], categories: [], hashtags: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/discover');
        setData(data);
      } catch (_e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-aura-bg pb-28 relative">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <header className="relative flex items-center justify-between px-5 pt-6">
        <div className="font-display font-black text-lg tracking-[0.3em] aura-text-gradient">DISCOVER</div>
        <LanguageToggle />
      </header>

      <main className="relative mx-auto max-w-md px-5 pt-4 space-y-6">
        <section className="grid grid-cols-2 gap-2" data-testid="discover-quicknav">
          <Link to="/events" data-testid="quick-events" className="aura-card p-3 flex items-center justify-between hover:border-aura-cyan/40 transition">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-aura-textDim">Agenda</div>
              <div className="font-display font-bold text-white text-sm">Eventos</div>
            </div>
            <div className="text-aura-cyan text-xl">📅</div>
          </Link>
          <Link to="/contests" data-testid="quick-contests" className="aura-card p-3 flex items-center justify-between hover:border-aura-pink/40 transition">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-aura-textDim">Ciudades</div>
              <div className="font-display font-bold text-white text-sm">Contiendas</div>
            </div>
            <div className="text-aura-pink text-xl">🎪</div>
          </Link>
        </section>
        {loading ? (
          <div className="text-center py-16 text-aura-textDim animate-aura-pulse text-4xl">⚡</div>
        ) : (
          <>
            {/* Hashtags */}
            {data.hashtags?.length > 0 && (
              <section>
                <h3 className="font-display font-bold text-white text-sm tracking-widest mb-2">{t('discover.hashtags')}</h3>
                <div className="flex gap-2 flex-wrap">
                  {data.hashtags.map((h) => (
                    <Link key={h.tag} to={`/tag/${h.tag}`} data-testid={`hashtag-${h.tag}`} className="aura-chip !text-sm">
                      #{h.tag} <span className="text-aura-textDim ml-1 font-mono text-[10px]">{h.count}</span>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Trending posts */}
            {data.trending?.length > 0 && (
              <section>
                <h3 className="font-display font-bold text-white text-sm tracking-widest mb-2">{t('discover.trending')}</h3>
                <div className="grid grid-cols-3 gap-1.5" data-testid="trending-grid">
                  {data.trending.map((p) => (
                    <Link key={p.post_id} to={`/post/${p.public_post_id}`} className="aspect-[9/16] relative overflow-hidden rounded-lg bg-aura-surface2 border border-white/5">
                      {p.media?.[0] ? (
                        <img src={resolveMediaUrl(p.media[0].thumbnail_url || p.media[0].url)} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                      ) : (
                        <div className="absolute inset-0 grid place-items-center px-2 text-xs text-white/80 text-center">{(p.text_body || p.caption || '').slice(0, 60)}</div>
                      )}
                      <div className="absolute bottom-1 left-1 right-1 flex items-center gap-1 text-white text-[10px]">
                        <Zap size={10} className="text-aura-cyan" />
                        <span className="font-mono">{formatAura(p.reactions_total || 0)}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Creators */}
            {data.creators?.length > 0 && (
              <section>
                <h3 className="font-display font-bold text-white text-sm tracking-widest mb-2">{t('discover.creators')}</h3>
                <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
                  {data.creators.map((u) => (
                    <Link key={u.user_id} to={`/u/${u.username}`} data-testid={`creator-${u.username}`} className="shrink-0 w-24 text-center">
                      <UserAvatar user={u} size="md" className="mx-auto" />
                      <div className="text-xs mt-2 truncate">@{u.username}</div>
                      <div className="text-[10px] text-aura-cyan font-mono">{formatAura(u.aura_points || 0)}</div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Categories */}
            {data.categories?.length > 0 && (
              <section>
                <h3 className="font-display font-bold text-white text-sm tracking-widest mb-2">{t('discover.categories')}</h3>
                <div className="grid grid-cols-2 gap-2">
                  {data.categories.map((c) => (
                    <Link key={c.category} to={`/tag/${c.category}`} className="aura-card p-3 flex items-center justify-between text-sm hover:border-aura-cyan/40 transition">
                      <span className="font-display capitalize">{c.category.replace('_', ' ')}</span>
                      <span className="text-[10px] text-aura-textDim">{c.count}</span>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
